import { useCallback, useEffect, useState } from "react";
import Product from "../../../../utilities/data/model/Product";
import { useDatabase, useGetEntity } from "../../../../utilities/data/hooks";
import { ProductOption } from "@towersystems/roam-common/lib/generated-types";

interface VariantPickerHandlers {
  onProductSelected: (product: Product) => void;
}

/**
 * Encapsulates the logic for picking product variants based on selected options.
 * State exposed for custom UI rendering.
 * @param productId The ID of the product to pick variants for
 * @returns A list of option values to select from, the current state of the variant picker, and actions to manipulate the state
 */
export const useVariantPicker = ({
  onProductSelected,
}: VariantPickerHandlers) => {
  const database = useDatabase();

  const [product, setProduct] = useState<Product | null>(null);
  const [variantProducts, setVariantProducts] = useState<Product[]>();
  const [selectedOptionValues, setSelectedOptionValues] = useState<string[]>(
    []
  );
  const [filteredVariantProducts, setFilteredVariantProducts] = useState<
    Product[]
  >([]);
  const [activeOptionIndex, setActiveOptionIndex] = useState(0);

  const [activeOption, setActiveOption] = useState<ProductOption | undefined>(
    undefined
  );
  const [selectableOptionValues, setSelectableOptionValues] = useState<
    string[]
  >([]);

  useEffect(() => {
    if (product && product.options.length > 0) {
      setActiveOption(product.options[activeOptionIndex]);
    }
  }, [product?.id, activeOptionIndex]);

  useEffect(() => {
    if (product?.hasVariants) {
      product.variantProducts
        .fetch()
        .then((i) => setVariantProducts(i.filter((p) => p.active)))
        .catch((err) => console.log(err));
    } else {
    }
  }, [product?.id]);

  useEffect(() => {
    if (variantProducts) {
      const q = variantProducts.filter((p) => {
        if (selectedOptionValues.length === 0) {
          return true;
        }
        // make sure the every selectedOptionValue is in the product's options
        return selectedOptionValues.every((o) => {
          return p.selectedOptions.find((po) => po.value === o);
        });
      });
      setFilteredVariantProducts(q);
    }
  }, [variantProducts, selectedOptionValues]);

  useEffect(() => {
    if (activeOption) {
      setSelectableOptionValues(
        activeOption.values.filter((o) => {
          return filteredVariantProducts.find((v) => {
            return v.selectedOptions.find((so) => {
              return so.value === o;
            });
          });
        })
      );
    }
  }, [filteredVariantProducts, activeOption]);

  useEffect(() => {
    if (
      product &&
      activeOptionIndex >= product?.options.length &&
      filteredVariantProducts.length === 1
    ) {
      const targetProduct = filteredVariantProducts[0];

      onProductSelected(targetProduct);
    }
  }, [filteredVariantProducts.length, activeOptionIndex]);

  const clearOptionValueAfterIndex = (index: number) => {
    setActiveOptionIndex(index);
    const updatedSelectedOptions = [...selectedOptionValues];
    updatedSelectedOptions.length = index;
    setSelectedOptionValues(updatedSelectedOptions);
  };

  const handleOnSelectableOptionPressed = (optionValue: string) => {
    const option = activeOption?.values.find((o) => optionValue == o);
    if (!option) return;
    setSelectedOptionValues([...selectedOptionValues, option]);
    setActiveOptionIndex(activeOptionIndex + 1);
  };

  const items =
    selectableOptionValues?.map((v) => ({
      key: v,
      title: v,
      onPress: () => handleOnSelectableOptionPressed(v),
    })) || [];

  const beginVariantPicker = useCallback(
    (newProductId: string) => {
      database
        .get<Product>("products")
        .find(newProductId)
        .catch(() => null)
        .then(setProduct);
    },
    [database]
  );

  return {
    beginVariantPicker,
    items,
    state: {
      product,
      activeOption,
      activeOptionIndex,
      selectableOptionValues,
      selectedOptionValues,
    },
    actions: {
      clearOptionValueAfterIndex,
    },
  };
};
